import { useState, useEffect } from "react";
import axios from "axios";
import {
  Scale,
  ArrowRight,
  CheckCircle,
  Circle,
  Loader2,
  AlertTriangle,
  TrendingUp,
  TrendingDown,
  Download,
  FileText,
  ChevronRight,
  Sparkles,
  Shield,
  DollarSign,
  Clock,
  ChevronDown,
  ChevronUp,
  ExternalLink
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Step indicator component
const StepIndicator = ({ currentStep, steps }) => (
  <div className="flex items-center justify-between mb-8">
    {steps.map((step, idx) => (
      <div key={idx} className="flex items-center">
        <div className={`
          flex items-center justify-center w-10 h-10 rounded-full border-2 
          ${idx < currentStep ? 'bg-green-500 border-green-500 text-white' : 
            idx === currentStep ? 'bg-primary border-primary text-primary-foreground' : 
            'border-muted-foreground text-muted-foreground'}
        `}>
          {idx < currentStep ? (
            <CheckCircle className="w-5 h-5" />
          ) : (
            <span className="font-semibold">{idx + 1}</span>
          )}
        </div>
        <span className={`ml-2 text-sm hidden sm:block ${idx <= currentStep ? 'text-foreground' : 'text-muted-foreground'}`}>
          {step}
        </span>
        {idx < steps.length - 1 && (
          <ChevronRight className="w-5 h-5 mx-2 text-muted-foreground" />
        )}
      </div>
    ))}
  </div>
);

// Urgency badge component
const UrgencyBadge = ({ urgency }) => {
  const colors = {
    HIGH: "bg-red-500",
    MEDIUM: "bg-amber-500",
    LOW: "bg-green-500",
  };
  return (
    <Badge className={`${colors[urgency] || "bg-gray-500"} text-white`}>
      {urgency}
    </Badge>
  );
};

// Action badge component
const ActionBadge = ({ action }) => {
  const colors = {
    SELL: "bg-red-500/10 text-red-500 border-red-500/30",
    REDUCE: "bg-orange-500/10 text-orange-500 border-orange-500/30",
    HOLD: "bg-gray-500/10 text-gray-500 border-gray-500/30",
    INCREASE: "bg-green-500/10 text-green-500 border-green-500/30",
    BUY: "bg-blue-500/10 text-blue-500 border-blue-500/30",
  };
  return (
    <Badge variant="outline" className={colors[action] || "border-gray-500"}>
      {action}
    </Badge>
  );
};

export const RebalanceWizard = ({ portfolioId, portfolioName, isOpen, onClose, onComplete }) => {
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [plan, setPlan] = useState(null);
  const [simulation, setSimulation] = useState(null);
  const [approvedRecs, setApprovedRecs] = useState([]);
  const [modifications, setModifications] = useState({});
  const [expandedStep, setExpandedStep] = useState(null);

  const STEPS = ["Analyze", "Review", "Simulate", "Approve", "Execute"];

  useEffect(() => {
    if (isOpen && portfolioId) {
      analyzePorfolio();
    }
  }, [isOpen, portfolioId]);

  const analyzePorfolio = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/rebalance-wizard/${portfolioId}/analyze`);
      setPlan(response.data);
      // Default: approve all non-HOLD recommendations
      const defaultApproved = response.data.recommendations
        ?.filter(r => r.action !== "HOLD")
        .map(r => r.fund_name) || [];
      setApprovedRecs(defaultApproved);
      setStep(1);
    } catch (err) {
      toast.error("Failed to analyze portfolio: " + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  const runSimulation = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/rebalance-wizard/${plan.plan_id}/simulate`);
      setSimulation(response.data.simulation_results);
      setStep(2);
    } catch (err) {
      toast.error("Simulation failed: " + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  const approvePlan = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/rebalance-wizard/${plan.plan_id}/approve`, {
        approved_recommendations: approvedRecs,
        modifications: modifications,
      });
      setPlan(response.data);
      setStep(3);
    } catch (err) {
      toast.error("Approval failed: " + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  const markComplete = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/rebalance-wizard/${plan.plan_id}/complete`);
      toast.success("Rebalance marked as complete! Refresh your portfolio to see updates.");
      onComplete?.();
      onClose();
    } catch (err) {
      toast.error("Failed to mark complete: " + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  const downloadPDF = () => {
    window.open(`${API}/rebalance-wizard/${plan.plan_id}/pdf`, "_blank");
  };

  const toggleRecommendation = (fundName) => {
    setApprovedRecs(prev => 
      prev.includes(fundName) 
        ? prev.filter(f => f !== fundName)
        : [...prev, fundName]
    );
  };

  const updateAmount = (fundName, amount) => {
    setModifications(prev => ({
      ...prev,
      [fundName]: parseFloat(amount) || 0
    }));
  };

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="rebalance-wizard-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Scale className="w-5 h-5" />
            Rebalance Wizard — {portfolioName}
          </DialogTitle>
        </DialogHeader>

        <StepIndicator currentStep={step} steps={STEPS} />

        {loading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
            <span className="ml-2">
              {step === 0 ? "Analyzing your portfolio with AI..." : 
               step === 2 ? "Running 5-year simulation..." : "Processing..."}
            </span>
          </div>
        )}

        {/* Step 1: Review Recommendations */}
        {!loading && step === 1 && plan && (
          <div className="space-y-6">
            {/* AI Summary */}
            <Card className="bg-primary/5 border-primary/20">
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <Sparkles className="w-5 h-5 text-primary mt-0.5" />
                  <div>
                    <span className="text-sm font-medium text-primary">AI Recommendation Summary</span>
                    <p className="text-sm mt-1">{plan.ai_summary}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Recommendations */}
            <div className="space-y-3">
              <h3 className="font-semibold flex items-center gap-2">
                <Shield className="w-4 h-4" /> Recommendations ({plan.recommendations?.length || 0})
              </h3>
              {plan.recommendations?.map((rec, idx) => (
                <Card key={idx} className={`${approvedRecs.includes(rec.fund_name) ? "border-primary" : "border-muted"}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <Checkbox
                        checked={approvedRecs.includes(rec.fund_name)}
                        onCheckedChange={() => toggleRecommendation(rec.fund_name)}
                        disabled={rec.action === "HOLD"}
                      />
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{rec.fund_name}</span>
                            <ActionBadge action={rec.action} />
                          </div>
                          <UrgencyBadge urgency={rec.urgency} />
                        </div>
                        {rec.amount && (
                          <div className="flex items-center gap-2 mb-2">
                            <DollarSign className="w-4 h-4 text-muted-foreground" />
                            <span className="font-medium">£{rec.amount?.toLocaleString()}</span>
                            {approvedRecs.includes(rec.fund_name) && rec.action !== "HOLD" && (
                              <Input
                                type="number"
                                placeholder="Modify amount"
                                className="w-32 h-8 text-sm"
                                defaultValue={rec.amount}
                                onChange={(e) => updateAmount(rec.fund_name, e.target.value)}
                              />
                            )}
                          </div>
                        )}
                        <p className="text-sm text-muted-foreground mb-2">{rec.reason}</p>
                        <div className="grid grid-cols-2 gap-2 text-xs">
                          <div className="bg-muted/30 p-2 rounded">
                            <span className="text-muted-foreground">Macro Context:</span>
                            <p>{rec.macro_context}</p>
                          </div>
                          <div className="bg-muted/30 p-2 rounded">
                            <span className="text-muted-foreground">Expected Impact:</span>
                            <p>{rec.expected_impact}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            <Button onClick={runSimulation} className="w-full" disabled={approvedRecs.length === 0}>
              Run Simulation <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        )}

        {/* Step 2: Simulation Results */}
        {!loading && step === 2 && simulation && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Current Portfolio */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Circle className="w-4 h-4" /> Current Portfolio
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">
                    £{simulation.current_portfolio?.total_value?.toLocaleString()}
                  </div>
                  <div className="text-sm text-muted-foreground mt-2">
                    5-Year Projection (Median): £{simulation.current_portfolio?.projected_5yr_median?.toLocaleString()}
                  </div>
                  <div className="flex justify-between text-xs text-muted-foreground mt-1">
                    <span>Worst: £{simulation.current_portfolio?.projected_5yr_worst?.toLocaleString()}</span>
                    <span>Best: £{simulation.current_portfolio?.projected_5yr_best?.toLocaleString()}</span>
                  </div>
                </CardContent>
              </Card>

              {/* Rebalanced Portfolio */}
              <Card className="border-green-500/30 bg-green-500/5">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2 text-green-500">
                    <TrendingUp className="w-4 h-4" /> After Rebalancing
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-green-500">
                    £{simulation.rebalanced_portfolio?.total_value?.toLocaleString()}
                  </div>
                  <div className="text-sm text-muted-foreground mt-2">
                    5-Year Projection (Median): £{simulation.rebalanced_portfolio?.projected_5yr_median?.toLocaleString()}
                  </div>
                  <div className="flex justify-between text-xs text-muted-foreground mt-1">
                    <span>Worst: £{simulation.rebalanced_portfolio?.projected_5yr_worst?.toLocaleString()}</span>
                    <span>Best: £{simulation.rebalanced_portfolio?.projected_5yr_best?.toLocaleString()}</span>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Improvement Card */}
            <Card className="bg-gradient-to-r from-green-500/10 to-blue-500/10 border-green-500/30">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <span className="text-sm text-muted-foreground">Projected 5-Year Improvement</span>
                    <div className="text-3xl font-bold text-green-500 flex items-center gap-2">
                      <TrendingUp className="w-6 h-6" />
                      +£{simulation.improvement?.median_gain?.toLocaleString()}
                    </div>
                    <div className="text-sm text-green-500">
                      +{simulation.improvement?.median_gain_pct}% better outcome
                    </div>
                  </div>
                  <Sparkles className="w-12 h-12 text-green-500/30" />
                </div>
              </CardContent>
            </Card>

            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setStep(1)}>
                Back to Review
              </Button>
              <Button onClick={approvePlan} className="flex-1">
                Approve & Get Instructions <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        )}

        {/* Step 3 & 4: Execution Steps */}
        {!loading && step === 3 && plan?.execution_steps && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold">Execution Instructions</h3>
                <p className="text-sm text-muted-foreground">
                  {plan.total_trades} trades • Est. {plan.estimated_time_minutes} minutes
                </p>
              </div>
              <Button variant="outline" onClick={downloadPDF}>
                <Download className="w-4 h-4 mr-2" /> Download PDF Guide
              </Button>
            </div>

            <Accordion type="single" collapsible className="space-y-2">
              {plan.execution_steps?.map((step, idx) => (
                <AccordionItem key={idx} value={`step-${idx}`} className="border rounded-lg px-4">
                  <AccordionTrigger className="hover:no-underline">
                    <div className="flex items-center gap-3">
                      <span className={`
                        flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium
                        ${expandedStep === idx ? "bg-primary text-primary-foreground" : "bg-muted"}
                      `}>
                        {step.step_number}
                      </span>
                      <div className="text-left">
                        <div className="font-medium">{step.title}</div>
                        {step.fund_name && (
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            {step.action && <ActionBadge action={step.action} />}
                            {step.amount && <span>£{step.amount?.toLocaleString()}</span>}
                          </div>
                        )}
                      </div>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="pt-2 pb-4">
                    <div className="pl-11 space-y-3">
                      <p className="text-sm text-muted-foreground">{step.description}</p>
                      <div className="bg-muted/30 p-4 rounded-lg">
                        <div className="flex items-center gap-2 mb-2">
                          <ExternalLink className="w-4 h-4 text-primary" />
                          <span className="text-sm font-medium text-primary">HL Instructions</span>
                        </div>
                        <pre className="text-xs whitespace-pre-wrap font-mono">
                          {step.hl_instruction}
                        </pre>
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>

            <Card className="bg-amber-500/10 border-amber-500/30">
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-amber-500 mt-0.5" />
                  <div className="text-sm">
                    <span className="font-medium text-amber-500">Important Reminder</span>
                    <p className="text-muted-foreground mt-1">
                      Always double-check amounts before confirming trades on HL. Fund prices change daily,
                      so actual amounts may vary slightly. Settlement typically takes 2-3 business days.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setStep(2)}>
                Back to Simulation
              </Button>
              <Button onClick={markComplete} className="flex-1" variant="default">
                <CheckCircle className="w-4 h-4 mr-2" /> Mark Rebalance Complete
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default RebalanceWizard;
